import xmlFieldKeyMapRaw from '../../../assets/rule-config/field-key-map-xml.json';

import { normalizeXmlFieldPersistPath } from './xml-field-key-map.flatten';

export interface PathBuildAttributeOption {
  name: string;
  label: string;
  dataType?: string;
}

export interface PathFilterRow {
  attribute: string;
  value: string;
}

/** One element along the canonical template path (Case root skipped in UI lists). */
export interface PathBuildElementStep {
  elementName: string;
  /** UI heading from JSON `node.label` when set; otherwise falls back to `elementName`. */
  displayLabel?: string;
  /** Optional override: force show/hide this repeatable row in Build Path. */
  buildPathStep?: boolean;
  isRepeatable: boolean;
  attributeOptions: PathBuildAttributeOption[];
  filters: PathFilterRow[];
  occurrenceIndex: number;
  /** HL7: component index for this segment (replaces attribute filters in Build Path UI). */
  hl7FieldIndex?: string;
  hl7ComponentIndex?: string;
}

export interface PathBuildModel {
  steps: PathBuildElementStep[];
  /** Indices into `steps` for UI — only these nodes are configurable (repeatable). */
  repeatableStepIndices: number[];
  leafAttribute: string;
  leafAttributeOptions: PathBuildAttributeOption[];
}

/**
 * Whether a repeatable step row should appear in the Build Path dialog.
 * Skips structural XML wrappers (repeatable but no label/attributes), e.g. ObservationSet.
 * HL7 parents with a `displayLabel` (e.g. OBR) still show even without attribute filters.
 */
function repeatableStepShowsInBuildPathDialog(model: PathBuildModel, idx: number): boolean {
  const s = model.steps[idx];
  if (!s?.isRepeatable) {
    return false;
  }
  if (s.buildPathStep === true) {
    return true;
  }
  if (s.buildPathStep === false) {
    return false;
  }
  const hasSchemaAttributes = (s.attributeOptions?.length ?? 0) > 0;
  const hasExistingFilters = (s.filters?.length ?? 0) > 0;
  if (hasSchemaAttributes || hasExistingFilters) {
    return true;
  }
  if ((s.displayLabel ?? '').trim()) {
    return true;
  }
  /* Terminal repeatable for the selected leaf (e.g. Observation) — always show when repeatable. */
  return idx === model.steps.length - 1;
}

/** Indices of repeatable steps (supports models built before `repeatableStepIndices` existed). */
export function getRepeatableStepIndices(model: PathBuildModel): number[] {
  const compute = () =>
    model.steps
      .map((s, i) => (repeatableStepShowsInBuildPathDialog(model, i) ? i : -1))
      .filter((i) => i >= 0);

  const raw = model.repeatableStepIndices;
  if (raw?.length) {
    const valid = raw.filter((idx) => idx >= 0 && idx < model.steps.length);
    // Recompute when persisted indices point to steps that no longer qualify for UI.
    const stillConfigurable = valid.filter((idx) => repeatableStepShowsInBuildPathDialog(model, idx));
    return stillConfigurable.length ? stillConfigurable : compute();
  }
  return compute();
}

interface TreeAttr {
  name: string;
  label?: string;
  dataType?: string;
}

interface TreeNode {
  name: string;
  label?: string;
  xpath?: string;
  isRepeatable?: boolean;
  buildPathStep?: boolean;
  discriminateByNodeAttributes?: boolean;
  attributes?: TreeAttr[];
  children?: TreeNode[];
}

function isXmlTreeDoc(raw: unknown): raw is TreeNode {
  if (!raw || typeof raw !== 'object') {
    return false;
  }
  const d = raw as TreeNode;
  return typeof d.name === 'string';
}

export function formatXmlAttributePredicate(
  attribute: string,
  rawValue: string,
  dataType?: 'string' | 'number'
): string {
  const attr = (attribute || '').trim();
  const v = (rawValue ?? '').toString().trim();
  if (!attr || v === '') {
    return '';
  }
  const numeric =
    dataType === 'number' ||
    (dataType !== 'string' && /^-?\d+(?:\.\d+)?$/.test(v));
  if (numeric && /^-?\d+(?:\.\d+)?$/.test(v)) {
    return `[@${attr}=${v}]`;
  }
  const escaped = v.replace(/'/g, "\\'");
  return `[@${attr}='${escaped}']`;
}

/** Parse ObservationDetails[@name='pc'][@foo=bar] Observation[2] style segments. */
export function parsePredicateElementSegment(token: string): {
  name: string;
  predicates: PathFilterRow[];
  occurrence?: number;
} {
  const t = token.trim();
  let i = 0;
  while (i < t.length && t[i] !== '[') {
    i++;
  }
  const name = t.slice(0, i).trim();
  let rest = t.slice(i);
  const predicates: PathFilterRow[] = [];
  const re =
    /^\[@([^\]=\s]+)\s*=\s*(?:'([^']*)'|"([^"]*)"|(\s*[^\]]+))\]/;

  while (true) {
    const m = re.exec(rest);
    if (!m) {
      break;
    }
    const attr = (m[1] || '').trim();
    const rawVal = (m[2] ?? m[3] ?? (m[4] ?? '').trim()) || '';
    const val = `${rawVal}`.trim().replace(/\\'/g, "'");
    predicates.push({ attribute: attr, value: val });
    rest = rest.slice(m[0].length);
  }
  let occurrence: number | undefined;
  const occ = /^\[\s*(\d+)\s*\]$/.exec(rest.trim());
  if (occ) {
    occurrence = parseInt(occ[1], 10);
  }
  return { name, predicates, occurrence };
}

function leafAttributeFromPersistedPath(path: string): string {
  const norm = normalizeXmlFieldPersistPath(path);
  const parts = norm.split('/').filter(Boolean);
  const last = parts[parts.length - 1] || '';
  return last.startsWith('@') ? last.replace(/^@+/, '').trim() : '';
}

/**
 * True when `persisted` matches flattened template path: same nodes/leaf attr, same predicate values,
 * and persisted may include extra filters (e.g. `Observation[@Name='x']` on top of template `Observation`).
 */
export function persistedXmlPathMatchesTemplate(templatePath: string, persistedPath: string): boolean {
  const template = normalizeXmlFieldPersistPath(templatePath);
  const persisted = normalizeXmlFieldPersistPath(persistedPath);
  if (!template || !persisted) {
    return false;
  }
  const tLeaf = leafAttributeFromPersistedPath(template);
  const pLeaf = leafAttributeFromPersistedPath(persisted);
  if (!tLeaf || !pLeaf || tLeaf.toLowerCase() !== pLeaf.toLowerCase()) {
    return false;
  }
  const tElements = template.split('/').filter((p) => p && !p.startsWith('@'));
  const pElements = persisted.split('/').filter((p) => p && !p.startsWith('@'));
  if (tElements.length !== pElements.length) {
    return false;
  }
  for (let i = 0; i < tElements.length; i++) {
    const tp = parsePredicateElementSegment(tElements[i]);
    const pp = parsePredicateElementSegment(pElements[i]);
    if (tp.name.toLowerCase() !== pp.name.toLowerCase()) {
      return false;
    }
    for (const pred of tp.predicates) {
      const attr = (pred.attribute || '').trim().toLowerCase();
      const val = `${pred.value ?? ''}`.trim();
      const match = pp.predicates.some(
        (x) => (x.attribute || '').trim().toLowerCase() === attr && `${x.value ?? ''}`.trim() === val
      );
      if (!match) {
        return false;
      }
    }
  }
  return true;
}

export function coalesceDataType(
  opt: PathBuildAttributeOption | undefined,
  raw: string
): 'string' | 'number' {
  if (opt?.dataType === 'number' || opt?.dataType === 'integer') {
    return 'number';
  }
  if (opt?.dataType === 'string' || opt?.dataType === 'date') {
    return 'string';
  }
  if (/^-?\d+(?:\.\d+)?$/.test(raw.trim())) {
    return 'number';
  }
  return 'string';
}

/** Ensure rows exist for flattened nodePredicates hints (routing map). */
export function mergeNodePredicateSpecsIntoSteps(
  model: PathBuildModel,
  specs: { node: string; attribute: string; value?: string }[]
): void {
  for (const spec of specs ?? []) {
    const step = model.steps.find((s) => s.elementName === spec.node);
    if (!step || !(spec.attribute || '').trim()) {
      continue;
    }
    const a = spec.attribute.trim();
    const has = step.filters.some((f) => (f.attribute || '').trim() === a);
    if (!has) {
      step.filters.push({ attribute: a, value: ((spec.value ?? '') || '').toString().trim() });
    }
  }
}

/**
 * Build a step model from an internal template path (no predicates), e.g. Case/…/Observation/@Value
 */
export function buildPathModelFromTemplatePath(templatePath: string): PathBuildModel | null {
  if (!isXmlTreeDoc(xmlFieldKeyMapRaw)) {
    return null;
  }
  const doc = xmlFieldKeyMapRaw as TreeNode;
  if ((doc.name || '') !== 'Case') {
    return null;
  }
  const norm = normalizeXmlFieldPersistPath(templatePath);
  const parts = norm.split('/').filter(Boolean);
  if (!parts.length) {
    return null;
  }
  const last = parts[parts.length - 1];
  if (!last.startsWith('@')) {
    return null;
  }
  const leafAttrName = last.replace(/^@/, '').trim();
  const elementParts = parts.slice(0, -1);
  let cursor: TreeNode = doc;
  let i = 0;
  if (elementParts[0] === 'Case') {
    i = 1;
  }
  const steps: PathBuildElementStep[] = [];
  for (; i < elementParts.length; i++) {
    const rawSeg = elementParts[i];
    const bareName = rawSeg.split('[')[0].trim().split('[@')[0].trim();
    const child = (cursor.children ?? []).find((c) => c.name === bareName);
    if (!child) {
      return null;
    }
    const displayLabel = (child.label || '').trim();
    steps.push({
      elementName: child.name,
      displayLabel: displayLabel || undefined,
      buildPathStep: child.buildPathStep,
      isRepeatable: !!child.isRepeatable,
      attributeOptions: (child.attributes ?? []).map((a) => ({
        name: a.name,
        label: (a.label || a.name).trim(),
        dataType: a.dataType,
      })),
      filters: [],
      occurrenceIndex: 1,
    });
    cursor = child;
  }
  const leafAttributeOptions = (cursor.attributes ?? []).map((a) => ({
    name: a.name,
    label: (a.label || a.name).trim(),
    dataType: a.dataType,
  }));
  const leafKnown = leafAttributeOptions.some((o) => o.name === leafAttrName);
  const model: PathBuildModel = {
    steps,
    repeatableStepIndices: [],
    leafAttribute: leafKnown ? leafAttrName : leafAttrName || (leafAttributeOptions[0]?.name ?? ''),
    leafAttributeOptions,
  };
  model.repeatableStepIndices = getRepeatableStepIndices(model);
  return model;
}

/** Element segments only (no @leaf), normalized and without leading Case root. */
export function elementSegmentsFromPersistedPath(path: string): string[] {
  const norm = normalizeXmlFieldPersistPath(path);
  let parts = norm.split('/').filter((p) => !p.startsWith('@'));
  if (parts[0] === 'Case') {
    parts = parts.slice(1);
  }
  return parts;
}

export function applyPersistedPathToModel(model: PathBuildModel, persistedPath: string): void {
  const norm = normalizeXmlFieldPersistPath(persistedPath);
  const nonAttrParts = elementSegmentsFromPersistedPath(norm);
  if (nonAttrParts.length !== model.steps.length) {
    return;
  }
  for (let idx = 0; idx < nonAttrParts.length; idx++) {
    const parsed = parsePredicateElementSegment(nonAttrParts[idx]);
    const step = model.steps[idx];
    if (parsed.name !== step.elementName) {
      return;
    }
    step.filters =
      parsed.predicates.filter(
        (r) => (r.attribute ?? '').trim() && `${r.value ?? ''}`.trim() !== ''
      ) ?? [];
    if (parsed.occurrence != null && Number.isFinite(parsed.occurrence) && parsed.occurrence >= 1) {
      step.occurrenceIndex = parsed.occurrence;
    }
  }
  const lm = /@([^/]+)$/.exec(norm);
  const la = lm ? lm[1].trim().replace(/^@+/, '') : '';
  if (la) {
    model.leafAttribute = la;
  }
}

export function pathModelHasRepeatableStep(model: PathBuildModel): boolean {
  return getRepeatableStepIndices(model).length > 0;
}

export function serializePathModelToPath(model: PathBuildModel): string {
  const segments = model.steps.map((s) => {
    let token = s.elementName;
    const nonemptyFilters = (s.filters ?? []).filter(
      (f) => (f.attribute ?? '').trim() && `${f.value ?? ''}`.trim() !== ''
    );
    for (const f of nonemptyFilters) {
      const opt = s.attributeOptions.find((o) => o.name === f.attribute.trim());
      token += formatXmlAttributePredicate(f.attribute.trim(), `${f.value}`.trim(), coalesceDataType(opt, f.value));
    }
    const hasFilters = nonemptyFilters.length > 0;
    const parsedOccurrence = Math.floor(Number(s.occurrenceIndex) || 0);
    // Do not force first occurrence [1] for unfiltered repeatables.
    // Keep an explicit index only when a previously saved path had [n] with n > 1.
    if (s.isRepeatable && !hasFilters && parsedOccurrence > 1) {
      token += `[${parsedOccurrence}]`;
    }
    return token;
  });
  const leaf = (model.leafAttribute || '').trim().replace(/^@+/, '');
  const inner = normalizeXmlFieldPersistPath([...segments, `@${leaf}`].join('/'));
  if (inner && !/^case\//i.test(inner)) {
    return normalizeXmlFieldPersistPath(`Case/${inner}`);
  }
  return inner;
}
